import _ from 'lodash';

import { jsonParse, jsonStringify } from 'utils/json';

import {
  BANNER_STATUS,
  GSE_FEED_KIND,
  GSE_PRICE_TYPE_LABELS,
  GSE_PULL_STATUS_LABELS,
  GSE_PULLS_MAX_PAGE_SIZE,
} from './marginCenter.constants';

// PPE 2.0 (DEV-4849): API vocabulary + pure transforms between the live Skynet.Pricing DTOs
// (MarginPlanModel / MarginCriteriaModel / CapturePolicyRowModel — camelCase + StringEnumConverter)
// and the Margin Center view models the wave-1 screens consume. Kept side-effect-free so the
// hydration ↔ persistence round-trip is unit-testable in both directions.

export const MARGIN_PLAN_STATUS = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  SUPERSEDED: 'Superseded',
  ARCHIVED: 'Archived',
  // DEV-5475: activated with a future EffectiveStartDate — becomes Active (and supersedes its
  // predecessor) once the date passes; the predecessor stays live until then.
  SCHEDULED: 'Scheduled',
};

export const MARGIN_SCOPE_TYPE = {
  SUB_COMPANY: 'SubCompany',
  BRANCH: 'Branch',
  BROKER: 'Broker',
};

export const MARGIN_TYPE = {
  POINTS: 'Points',
  BPS: 'Bps',
  DOLLARS: 'Dollars',
};

// LEN-293: how a floor/ceiling is expressed. Dollar-based limits resolve to bps per loan at quote time.
export const MARGIN_LIMIT_UNIT = {
  BPS: 'Bps',
  DOLLARS: 'Dollars',
  DOLLARS_PLUS_BPS: 'DollarsPlusBps',
};

export const MARGIN_AMOUNT_TYPE = {
  FIXED: 'Fixed',
  PERCENT_OF_LOAN: 'PercentOfLoan',
};

export const CAPTURE_COMPONENT_TYPE = {
  SPECIFIED_POOL: 'SpecifiedPool',
  SRP_PREMIUM: 'SrpPremium',
  EXECUTION_SPREAD: 'ExecutionSpread',
};

export const CAPTURE_SCOPE_TYPE = {
  COMPANY: 'Company',
  CHANNEL: 'Channel',
  SUB_COMPANY: 'SubCompany',
  BRANCH: 'Branch',
  PROGRAM_GROUP: 'ProgramGroup',
  PROGRAM: 'Program',
};

// A blank form input is "not set", the same as a missing value.
const toNumber = value => (_.isNil(value) || value === '' ? null : Number(value));

const mapLimitsToView = source => ({
  floorUnit: source.floorUnit ?? MARGIN_LIMIT_UNIT.BPS,
  floorBps: Number(source.floorBps),
  floorDollars: toNumber(source.floorDollars),
  ceilingUnit: source.ceilingUnit ?? MARGIN_LIMIT_UNIT.BPS,
  ceilingBps: Number(source.ceilingBps),
  ceilingDollars: toNumber(source.ceilingDollars),
});

const mapLimitsToRequest = view => ({
  floorUnit: view.floorUnit ?? MARGIN_LIMIT_UNIT.BPS,
  floorBps: view.floorBps,
  floorDollars: view.floorDollars ?? null,
  ceilingUnit: view.ceilingUnit ?? MARGIN_LIMIT_UNIT.BPS,
  ceilingBps: view.ceilingBps,
  ceilingDollars: view.ceilingDollars ?? null,
});

// --- Plan ↔ band (Console) ---------------------------------------------------

const mapScopeToOverride = scope => ({
  marginPlanScopeId: scope.marginPlanScopeId ?? null,
  scopeType: scope.scopeType,
  scopeRefId: scope.scopeRefId ?? null,
  ...mapLimitsToView(scope),
  minDollar: toNumber(scope.minDollarPerLoan),
  maxDollar: toNumber(scope.maxDollarPerLoan),
});

const mapOverrideToScopeRequest = override => ({
  scopeType: override.scopeType,
  scopeRefId: override.scopeRefId ?? null,
  ...mapLimitsToRequest(override),
  minDollarPerLoan: override.minDollar ?? null,
  maxDollarPerLoan: override.maxDollar ?? null,
});

// DEV-4977/MC-11: a per-program guardrail band + lock-days term under a MarginPlan, parallel to
// mapScopeToOverride but on the program axis (ProgramId) rather than the org-hierarchy axis.
const mapProgramBandToView = band => ({
  marginPlanProgramBandId: band.marginPlanProgramBandId ?? null,
  programId: band.programId,
  ...mapLimitsToView(band),
  lockDays: Number(band.lockDays),
});

const mapViewToProgramBandRequest = band => ({
  programId: band.programId,
  ...mapLimitsToRequest(band),
  lockDays: band.lockDays,
});

export const mapPlanToBand = plan => ({
  marginPlanId: plan.marginPlanId,
  channelId: plan.channelId,
  name: plan.name,
  ...mapLimitsToView(plan),
  minDollar: toNumber(plan.minDollarPerLoan),
  maxDollar: toNumber(plan.maxDollarPerLoan),
  effectiveStartDate: plan.effectiveStartDate,
  status: plan.status,
  supersedesPlanId: plan.supersedesPlanId ?? null,
  scopes: (plan.scopes ?? []).map(mapScopeToOverride),
  programBands: (plan.programBands ?? []).map(mapProgramBandToView),
});

// The delegated-override scopes of an EXISTING plan, in the request/band override shape — so a band-only
// update can pass the plan's persisted scopes back through and NOT wipe them (the server PUT full-replaces
// scopes: MarginPlanService.ReplaceScopes clears then rebuilds from request.Scopes).
export const mapPlanToScopeOverrides = plan => (plan?.scopes ?? []).map(mapScopeToOverride);

// The per-program bands of an EXISTING plan, in the view shape ConsolePage's editor hydrates from.
export const mapPlanToProgramBands = plan => (plan?.programBands ?? []).map(mapProgramBandToView);

export const mapBandToPlanRequest = band => ({
  channelId: band.channelId,
  name: band.name,
  ...mapLimitsToRequest(band),
  minDollarPerLoan: band.minDollar ?? null,
  maxDollarPerLoan: band.maxDollar ?? null,
  effectiveStartDate: band.effectiveStartDate,
  supersedesPlanId: band.supersedesPlanId ?? null,
  scopes: (band.scopes ?? []).map(mapOverrideToScopeRequest),
  programBands: (band.programBands ?? []).map(mapViewToProgramBandRequest),
});

// --- Criteria ↔ rule (Rules) -------------------------------------------------

export const mapCriteriaToRule = criteria => ({
  id: criteria.criteriaId,
  name: criteria.name,
  condition: criteria.userFriendlyExpression || '',
  amountBps: Number(criteria.amount),
  enabled: !!criteria.enabled,
  expression: criteria.expression ?? '',
  marginType: criteria.marginType ?? MARGIN_TYPE.BPS,
  marginAmountType: criteria.marginAmountType ?? MARGIN_AMOUNT_TYPE.FIXED,
  parentCriteriaId: criteria.parentCriteriaId ?? null,
});

// The backend (GenericCriteriaService.ValidateExpressionValuesConsistent) requires Expression and
// UserFriendlyExpression to be BOTH empty or BOTH non-empty — so a friendly label without an expression
// tree (or vice-versa) 409s. Enforce that here: an expression carries the condition as its label (falling
// back to the raw expression when no label was given); no expression = an unconditional (always-on) rule
// with both fields empty.
export const mapRuleToCriteriaRequest = rule => {
  const expression = (rule.expression ?? '').trim();
  const condition = (rule.condition ?? '').trim();

  return {
    amount: rule.amountBps,
    marginType: rule.marginType ?? MARGIN_TYPE.BPS,
    marginAmountType: rule.marginAmountType ?? MARGIN_AMOUNT_TYPE.FIXED,
    parentCriteriaId: rule.parentCriteriaId ?? null,
    name: rule.name,
    enabled: rule.enabled,
    expression,
    userFriendlyExpression: expression ? condition || expression : '',
  };
};

// --- Capture row ↔ view (Capture) --------------------------------------------

export const mapCaptureRowToView = row => ({
  id: row.capturePolicyRowId,
  componentType: row.componentType,
  scopeType: row.scopeType,
  scopeRefId: row.scopeRefId ?? null,
  passThroughPercent: Number(row.passThroughPercent),
  dollarCap: toNumber(row.dollarCap),
  dollarFloor: toNumber(row.dollarFloor),
  effectiveStartDate: row.effectiveStartDate,
});

export const mapViewToCaptureRowRequest = view => ({
  componentType: view.componentType,
  scopeType: view.scopeType,
  scopeRefId: view.scopeRefId ?? null,
  passThroughPercent: view.passThroughPercent,
  dollarCap: view.dollarCap ?? null,
  dollarFloor: view.dollarFloor ?? null,
  effectiveStartDate: view.effectiveStartDate,
});

// --- Guardrail config version ↔ view (Guardrails) ----------------------------

const PROGRAM_PCT_TO_FRACTION = 100;

const parseJsonOrDefault = (json, fallback) => {
  if (_.isNil(json) || json === '') {
    return fallback;
  }

  try {
    return jsonParse(json);
  } catch {
    return fallback;
  }
};

// DEV-5474: how activating a margin plan interacts with maker-checker approval. Serialized as a
// PascalCase string on the wire (StringEnumConverter) — ThresholdOnly is the platform default and the
// owner-decided rollout baseline (DEV-5452), so an unset value resolves to it in both directions.
export const MARGIN_ACTIVATION_APPROVAL_MODE = {
  THRESHOLD_ONLY: 'ThresholdOnly',
  ALWAYS_ON_ACTIVATE: 'AlwaysOnActivate',
  THRESHOLD_PLUS_FIRST_ACTIVATION: 'ThresholdPlusFirstActivation',
};

export const mapGuardrailRowToView = row => ({
  id: row.marginGuardrailConfigId,
  channelId: row.channelId ?? null,
  version: row.version,
  effectiveFrom: row.effectiveFrom,
  supersedesConfigId: row.supersedesConfigId ?? null,
  activationApprovalMode:
    row.activationApprovalMode ?? MARGIN_ACTIVATION_APPROVAL_MODE.THRESHOLD_ONLY,
  makerCheckerBps: Number(row.makerCheckerBpsThreshold),
  makerCheckerProgramPct: Number(row.makerCheckerProgramPctThreshold) * PROGRAM_PCT_TO_FRACTION,
  // DEV-4977/MC-10: the five scalars the wave-3 model didn't yet persist — below-floor-always-
  // maker-checker, the corporate hard-floor default, whether bands are HARD/SOFT by default, and the
  // republish mode + hourly rate limit. Wire field names are MarginGuardrailConfigRowModel's own
  // (BelowFloorLock / CorporateFloor / RepublishRateLimit) — kept distinct on the view side
  // (belowFloorAlwaysMakerChecker / corporateFloorBps / republishRateLimitPerHour) for on-page clarity.
  belowFloorAlwaysMakerChecker: !!row.belowFloorLock,
  corporateFloorBps: toNumber(row.corporateFloor),
  bandsHardByDefault: !!row.bandsHardByDefault,
  maxDeviationBps: Number(row.maxDeviationBps),
  staleness: parseJsonOrDefault(row.stalenessHaircutJson, {}),
  republishThresholdBps: Number(row.republishThresholdBps),
  republishMode: row.republishMode ?? null,
  republishRateLimitPerHour: toNumber(row.republishRateLimit),
  killSwitchReasonCodes: parseJsonOrDefault(row.killSwitchReasonCodesJson, []),
  concessionBudgets: parseJsonOrDefault(row.concessionBudgetsJson, []),
  createdDate: row.createdDate,
});

// The staleness blob keeps its toggles as they are and sends its numbers as numbers, leaving out unset ones.
const STALENESS_NUMBER_KEYS = ['staleMin', 'staleHaircut', 'deskWidenMax', 'srpCadenceHrs'];

const mapStalenessToRequest = staleness =>
  _.omitBy(
    {
      ...staleness,
      ..._.mapValues(_.pick(staleness, STALENESS_NUMBER_KEYS), toNumber),
    },
    _.isNil,
  );

// Unset values are left out rather than sent as null, so the server applies its own defaults (a null
// republish mode or rate limit is rejected outright).
export const mapGuardrailViewToVersionRequest = view => {
  const programPct = toNumber(view.makerCheckerProgramPct);

  return _.omitBy(
    {
      channelId: view.channelId ?? null,
      effectiveFrom: view.effectiveFrom ?? null,
      activationApprovalMode:
        view.activationApprovalMode ?? MARGIN_ACTIVATION_APPROVAL_MODE.THRESHOLD_ONLY,
      makerCheckerBpsThreshold: toNumber(view.makerCheckerBps),
      makerCheckerProgramPctThreshold: _.isNil(programPct)
        ? null
        : programPct / PROGRAM_PCT_TO_FRACTION,
      belowFloorLock: !!view.belowFloorAlwaysMakerChecker,
      corporateFloor: toNumber(view.corporateFloorBps),
      bandsHardByDefault: !!view.bandsHardByDefault,
      maxDeviationBps: toNumber(view.maxDeviationBps),
      stalenessHaircutJson: jsonStringify(mapStalenessToRequest(view.staleness ?? {})),
      republishThresholdBps: toNumber(view.republishThresholdBps),
      republishMode: view.republishMode ?? null,
      republishRateLimit: toNumber(view.republishRateLimitPerHour),
      killSwitchReasonCodesJson: jsonStringify(view.killSwitchReasonCodes ?? []),
      concessionBudgetsJson: jsonStringify(view.concessionBudgets ?? []),
    },
    (value, key) => _.isNil(value) && key !== 'channelId' && key !== 'effectiveFrom',
  );
};

// --- Pricing calendar config version ↔ view (Guardrails · Pricing Calendar tab) ---------------
// DEV-4977/MC-13: a real, versioned, effective-dated entity of its own (PricingCalendarConfigController)
// — company-wide only (no channel scope), reusing the guardrail governance claim group but NOT part of
// MarginGuardrailConfig. Replaces the wave-1 approach of a `pricingCalendarJson` blob folded into the
// guardrail version (that field never existed on the real MarginGuardrailConfigRowModel).

const mapHolidayToView = holiday => ({
  id: holiday.pricingCalendarHolidayId ?? null,
  holidayDate: holiday.holidayDate,
  name: holiday.name,
  isMarketClosed: !!holiday.isMarketClosed,
  isEarlyClose: !!holiday.isEarlyClose,
  earlyCloseCutoffTime: holiday.earlyCloseCutoffTime ?? null,
  note: holiday.note ?? '',
});

const mapViewToHolidayRequest = holiday => ({
  holidayDate: holiday.holidayDate,
  name: holiday.name,
  isMarketClosed: !!holiday.isMarketClosed,
  isEarlyClose: !!holiday.isEarlyClose,
  earlyCloseCutoffTime: holiday.earlyCloseCutoffTime || null,
  note: holiday.note || null,
});

export const mapPricingCalendarRowToView = row => ({
  id: row.pricingCalendarConfigId,
  version: row.version,
  effectiveFrom: row.effectiveFrom,
  supersedesConfigId: row.supersedesConfigId ?? null,
  afterHoursWidenEnabled: !!row.afterHoursWidenEnabled,
  weekendHolidayHoldEnabled: !!row.weekendHolidayHoldEnabled,
  sifmaEarlyCloseEnabled: !!row.sifmaEarlyCloseEnabled,
  sifmaEarlyCloseCutoffTime: row.sifmaEarlyCloseCutoffTime,
  holidays: (row.holidays ?? []).map(mapHolidayToView),
  createdDate: row.createdDate,
});

export const mapPricingCalendarViewToVersionRequest = view => ({
  effectiveFrom: view.effectiveFrom ?? null,
  afterHoursWidenEnabled: !!view.afterHoursWidenEnabled,
  weekendHolidayHoldEnabled: !!view.weekendHolidayHoldEnabled,
  sifmaEarlyCloseEnabled: !!view.sifmaEarlyCloseEnabled,
  sifmaEarlyCloseCutoffTime: view.sifmaEarlyCloseCutoffTime,
  holidays: (view.holidays ?? []).map(mapViewToHolidayRequest),
});

// --- Margin change request ↔ view (Preview maker-checker) --------------------

export const MARGIN_CHANGE_REQUEST_STATE = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'PendingApproval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PUBLISHED: 'Published',
  WITHDRAWN: 'Withdrawn',
};

export const mapChangeRequestRowToView = row => ({
  id: row.marginChangeRequestId,
  channelId: row.channelId,
  marginPlanId: row.marginPlanId ?? null,
  requestedByUserId: row.requestedByUserId,
  checkedByUserId: row.checkedByUserId ?? null,
  publishedByUserId: row.publishedByUserId ?? null,
  state: row.state,
  thresholdTripReason: row.thresholdTripReason ?? null,
  requestedAtUtc: row.requestedAtUtc,
  decidedAtUtc: row.decidedAtUtc ?? null,
  publishedAtUtc: row.publishedAtUtc ?? null,
  decisionNote: row.decisionNote ?? null,
});

// --- Loan concession ↔ view (Concession Queue) -------------------------------
// DEV-4977/MC-12: mirrors Skynet.Pricing's LoanConcessionRequestRowModel returned by
// LoanConcessionRequestController (route `loan-concession-requests`, list at `/pending`). The state
// machine is FLAT — `state` is Requested/Approved/Rejected (LoanConcessionRequestState), there is no
// nested `decision` object. Money field is `giveUpDollars`; timestamps are `requestedAtUtc`/`decidedAtUtc`;
// the request carries real `borrowerDisplayName`/`loanOfficerDisplayName` and a computed `budgetStatus`.

export const MARGIN_CONCESSION_DECISION_STATE = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const MARGIN_CONCESSION_REASON_CODE = {
  COMPETITIVE_MATCH: 'CompetitiveMatch',
  RELATIONSHIP_PRICING: 'RelationshipPricing',
  LOYALTY_DISCOUNT: 'LoyaltyDiscount',
  ERROR_CORRECTION: 'ErrorCorrection',
  RETENTION_SAVE: 'RetentionSave',
  OTHER: 'Other',
};

export const mapConcessionRowToView = row => ({
  id: row.loanConcessionRequestId,
  companyId: row.companyId ?? null,
  loanId: row.loanId,
  applicationId: row.applicationId ?? null,
  branchId: row.branchId ?? null,
  requestedByUserId: row.requestedByUserId,
  checkedByUserId: row.checkedByUserId ?? null,
  requestedBps: Number(row.requestedBps),
  requestedDollars: toNumber(row.giveUpDollars),
  reasonCode: row.reasonCode,
  reasonText: row.reasonText ?? '',
  borrowerDisplayName: row.borrowerDisplayName ?? '',
  loanOfficerDisplayName: row.loanOfficerDisplayName ?? '',
  lockExpiresAt: row.lockExpiresAtUtc ?? null,
  decisionState: row.state,
  decisionNote: row.decisionNote ?? null,
  createdDate: row.requestedAtUtc,
  decidedDate: row.decidedAtUtc ?? null,
  loanCustomAdjustmentId: row.loanCustomAdjustmentId ?? null,
  budgetStatus: row.budgetStatus ?? null,
});

// --- Freddie connection ↔ view (Settings) ------------------------------------

// The environment (uat|prod) and the requesting-party account identifier are straight pass-throughs
// of the FreddieConnection DTO's `environment` / `requestingPartyAccountIdentifier` fields — real
// (possibly null/unset) server values, never seeded defaults, per the same no-seed-persistence
// discipline as the rest of this page's state (see SettingsPage.utils.js). Per the owner-decided
// identity split (DEV-4916 follow-up), both are per-client, non-secret, and client-editable here —
// unlike the platform-managed credentials/org id, which stay internal-admin only.
export const mapFreddieConnectionToView = connection => ({
  enabled: !!connection.enabled,
  environment: connection.environment ?? null,
  sellerNumber: connection.sellerIds?.[0] ?? '',
  requestingPartyAccountIdentifier: connection.requestingPartyAccountIdentifier ?? '',
  poolTypes: connection.expectedPoolBuckets ?? [],
  products: connection.expectedProducts ?? [],
  priceTypes: connection.enabledPriceTypes ?? [],
  servicerIds: connection.expectedServicerIds ?? [],
});

// --- Fair-lending surveillance cohorts (Surveillance) ------------------------
//
// DEV-4979 (MC-17): cohorts here are k-anonymized aggregates over a HMDA protected-class dimension
// computed upstream in Skynet.Loans / a governed read-model, never raw race/ethnicity/sex — see
// GsePricingAnalyticsController's surveillance endpoints and the (separate, human-merge-gated)
// migration PR. This mapper is a strict ALLOWLIST: it reads only aggregate numeric/boolean fields off
// the API row (loan count, bps, percentages, a variance flag) and never passes through a raw category
// value — the on-screen cohort label is always synthesized from ordinal position ("Cohort A", "Cohort
// B", …), never taken from server-supplied text, so an upstream mistake that leaked a protected-class
// value could not surface in this UI.
const COHORT_ORDINAL_LABELS = [
  'Cohort A',
  'Cohort B',
  'Cohort C',
  'Cohort D',
  'Cohort E',
  'Cohort F',
];

export const cohortOrdinalLabel = index => COHORT_ORDINAL_LABELS[index] ?? `Cohort ${index + 1}`;

// DEV-4979/MC-16: one k-anonymized cohort row from Pricing's FairLendingSurveillanceResult
// (GET /api/pricing/analytics/surveillance). Margin is an AGGREGATE mean ± population std-dev (never
// raw min/max — those are individuals' exact values); approvalRatePct is null when the decided
// sub-population was under the k-anon floor (the server suppresses it — render as "—", never 0).
export const mapCohortSurveillanceRowToView = (row, index) => ({
  id: `${row.cohort ?? cohortOrdinalLabel(index)}-${index}`,
  cohort: row.cohort ?? cohortOrdinalLabel(index),
  loanCount: Number(row.sampleCount),
  meanMarginBps: Number(row.meanMarginBps),
  marginStdDevBps: Number(row.marginStdDevBps),
  approvalRatePct: row.approvalRatePct == null ? null : Number(row.approvalRatePct),
  flag: !!row.flagged,
});

// The full surveillance result: cross-cohort KPIs are taken from the SERVER (which computes them
// k-safely — parity over reported rates only, dispersion as the spread of cohort means), never
// re-derived on the client.
export const mapSurveillanceResultToView = result => ({
  marginDispersionBps: Number(result?.marginDispersionBps ?? 0),
  approvalParityPts: Number(result?.approvalParityPts ?? 0),
  cohortsFlagged: Number(result?.cohortsFlagged ?? 0),
  loansReviewed: Number(result?.loansReviewed ?? 0),
  cohorts: (result?.cohorts ?? []).map(mapCohortSurveillanceRowToView),
});

export const mapViewToFreddieConnectionRequest = view => ({
  enabled: view.enabled,
  environment: view.environment ?? null,
  sellerIds: view.sellerNumber ? [view.sellerNumber] : [],
  requestingPartyAccountIdentifier: view.requestingPartyAccountIdentifier ?? '',
  expectedProducts: view.products ?? [],
  expectedPoolBuckets: view.poolTypes ?? [],
  expectedServicerIds: view.servicerIds ?? [],
  enabledPriceTypes: view.priceTypes ?? [],
});

// --- Desk status (Cockpit — MC-3/MC-4/MC-5/MC-8) -----------------------------

// Newtonsoft (no custom TimeSpan converter — Program.cs) serializes TimeSpan via .NET's default
// "c"-format ToString(): "[-][d.]hh:mm:ss[.fffffff]". Returns whole minutes (sign-preserved), or null
// for a missing/unparseable value — never throws on an unexpected shape.
const TIMESPAN_PATTERN = /^(-)?(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})/;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;

export const parseTimeSpanMinutes = value => {
  if (_.isNil(value)) {
    return null;
  }

  const match = TIMESPAN_PATTERN.exec(value);

  if (!match) {
    return null;
  }

  const [, sign, days, hours, minutes] = match;
  const total =
    (Number(days) || 0) * MINUTES_PER_DAY + Number(hours) * MINUTES_PER_HOUR + Number(minutes);

  return sign ? -total : total;
};

// "130 min" -> "2h 10m"; "45 min" -> "45m". Used for srpAge, which (unlike baseAgeMinutes) CockpitPage
// renders as a pre-formatted string.
export const formatDuration = minutes => {
  if (minutes < MINUTES_PER_HOUR) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const remainder = minutes % MINUTES_PER_HOUR;

  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

// CockpitPage's suspect-pull tone check is `item.sla.startsWith('Overdue')`.
export const formatSlaRemaining = value => {
  const minutes = parseTimeSpanMinutes(value);

  if (_.isNil(minutes)) {
    return '—';
  }

  return minutes < 0 ? `Overdue ${Math.abs(minutes)}m` : `${formatDuration(minutes)} left`;
};

// The "primary" desk channel — what the banner leads with. A published sheet is the only reliable
// signal of a live channel (ChannelName is free-text: a "Retail eLEND" company would miss an exact
// "Retail" match and fall through to a junk TestChannel), so: among channels that actually have a
// published sheet, prefer one whose name reads as retail; else the first channel with a sheet; else
// the first channel; else nothing.
const PRIMARY_CHANNEL_HINT = 'retail';

const hasPublishedSheet = channel => !_.isNil(channel.publishedVersion);

const resolvePrimaryChannel = channels => {
  const withSheet = channels.filter(hasPublishedSheet);
  const pool = withSheet.length ? withSheet : channels;
  const retail = pool.find(channel =>
    (channel.channelName ?? '').toLowerCase().includes(PRIMARY_CHANNEL_HINT),
  );

  return retail ?? pool[0] ?? null;
};

// ChannelDeskStatusModel -> CockpitPage/MarginBanner's channel row shape. RateSheetVersionModel carries
// only Title + DateUploaded (no distinct effective date), so effectiveDate surfaces DateUploaded — the
// moment the Live sheet started quoting.
export const mapDeskStatusChannelToView = channel => ({
  id: channel.channelId,
  name: channel.channelName,
  sheetTitle: channel.publishedVersion?.title ?? null,
  effectiveDate: channel.publishedVersion?.dateUploaded ?? null,
  hasPublishedSheet: hasPublishedSheet(channel),
  candidatePending: !_.isNil(channel.candidateVersion),
  // No per-channel health signal exists on CommandCenterDeskStatusModel today — left null (falls
  // through channelTone's default "fern"/ok tone) rather than fabricating paused/stale.
  status: null,
});

// GsePricingPullReviewItem -> CockpitPage's suspect-pull row shape.
export const mapQuarantineItemToView = item => ({
  id: item.gsePricingPullId,
  description: item.gapSummary || `${item.sellerId} · ${item.channel} pull held for review`,
  check: (item.trippedChecks ?? []).map(check => check.reason).join('; ') || 'Held for review',
  status: (item.status ?? '').toLowerCase(),
  sla: formatSlaRemaining(item.slaRemaining),
});

// The freshest (lowest-age) feed of a kind — the pull actually serving live pricing. Feeds with an
// unparseable age are dropped so a genuinely-freshest feed never loses to one whose age we can't read.
const pickFreshestFeed = feeds =>
  (feeds ?? []).reduce(
    (best, candidate) => {
      const age = parseTimeSpanMinutes(candidate.age);

      if (_.isNil(age) || (best.feed && age >= best.ageMinutes)) {
        return best;
      }

      return { feed: candidate, ageMinutes: age };
    },
    { feed: null, ageMinutes: null },
  );

// Aggregates the raw GseFeedFreshness[] into the single Base/SRP figures the banner/tiles render.
// baseAgeMinutes AND baseStale both come from the SAME freshest Base feed (the pull serving live
// pricing), so the desk's age and staleness always describe one feed — a stale, older Base feed held
// OUT of pricing (e.g. a Quarantined seller) no longer flags the live desk STALE while its age reads
// fresh (DEV-5660). SRP stays worst-case (oldest age + any-stale) for risk surfacing.
export const aggregateFreshness = freshness => {
  const list = freshness ?? [];
  const base = list.filter(feed => feed.kind === GSE_FEED_KIND.BASE);
  const srp = list.filter(feed => feed.kind === GSE_FEED_KIND.SRP);

  const freshestBase = pickFreshestFeed(base);
  const srpAges = srp.map(feed => parseTimeSpanMinutes(feed.age)).filter(age => !_.isNil(age));

  return {
    baseAgeMinutes: freshestBase.ageMinutes,
    srpAgeMinutes: srpAges.length ? Math.max(...srpAges) : null,
    baseStale: freshestBase.feed ? !!freshestBase.feed.isStale : false,
    srpStale: srp.some(feed => feed.isStale),
  };
};

export const resolveBannerStatus = ({ hasAnyData, baseStale, srpStale }) => {
  if (!hasAnyData) {
    return null;
  }

  return baseStale || srpStale ? BANNER_STATUS.STALE : BANNER_STATUS.LIVE;
};

// Composes CommandCenterDeskStatusModel (MC-3) + GseFeedFreshness[] (MC-4) + GsePricingPullReviewItem[]
// (MC-5) into the single flat view MarginBanner/CockpitPage already consume — each input degrades
// independently to null/[] (see getDeskStatusAction), so a missing piece here always maps to its honest
// empty value, matching EMPTY_DESK_STATUS exactly when everything is unavailable.
export const mapDeskStatusToView = ({ deskStatus, freshness, quarantine, quarantineTotal }) => {
  const rawChannels = deskStatus?.channels ?? [];
  const channels = rawChannels.map(mapDeskStatusChannelToView);
  const primary = resolvePrimaryChannel(rawChannels);
  const { baseAgeMinutes, srpAgeMinutes, baseStale, srpStale } = aggregateFreshness(freshness);
  const hasAnyData = rawChannels.length > 0 || (freshness ?? []).length > 0;

  return {
    bannerStatus: resolveBannerStatus({ hasAnyData, baseStale, srpStale }),
    primaryChannelName: primary?.channelName ?? null,
    sheetTitle: primary?.publishedVersion?.title ?? null,
    primaryEffective: primary?.publishedVersion?.dateUploaded ?? null,
    // PublishedBy is the publishing user's Guid (only set when the channel's latest margin change
    // request was published) — the banner never renders it raw; publishedAt drives the relative time.
    publishedBy: primary?.publishedBy ?? null,
    publishedAt: primary?.publishedAt ?? null,
    baseAgeMinutes,
    baseStale,
    srpAge: _.isNil(srpAgeMinutes) ? null : formatDuration(srpAgeMinutes),
    srpStale,
    channels,
    // MC-9 (deferred): no reprice-log source endpoint exists yet.
    repriceLog: [],
    quarantine: (quarantine ?? []).map(mapQuarantineItemToView),
    quarantineTotal: quarantineTotal ?? null,
    // Without the server total, a full page of held pulls means there may be more than were read.
    isQuarantineTotalLowerBound:
      _.isNil(quarantineTotal) && (quarantine ?? []).length >= GSE_PULLS_MAX_PAGE_SIZE,
    freshnessFeeds: freshness ?? [],
  };
};

// Anchors an illustrative sample-attribution request (RulesPage MC-6) to a REAL, currently-quotable
// pull instead of a fabricated id — the freshest Base-kind feed's GsePricingPullId. Returns null when
// no Base feed has produced a Quotable pull yet (honest "no sample available" upstream).
export const resolveSampleGsePricingPullId = freshnessFeeds => {
  const baseFeeds = (freshnessFeeds ?? [])
    .filter(feed => feed.kind === GSE_FEED_KIND.BASE)
    .map(feed => ({ id: feed.gsePricingPullId, minutes: parseTimeSpanMinutes(feed.age) }))
    // parseTimeSpanMinutes returns null for an unparseable age, and `null < N` coerces null to 0 —
    // dropping unparseable feeds up front keeps a genuinely-freshest feed from losing to one whose
    // age we simply couldn't read.
    .filter(feed => !_.isNil(feed.minutes));

  if (!baseFeeds.length) {
    return null;
  }

  return baseFeeds.reduce((freshest, feed) => (feed.minutes < freshest.minutes ? feed : freshest))
    .id;
};

// --- Feeds & Sources pull data (LEN-88) --------------------------------------

const priceTypeLabel = priceType => GSE_PRICE_TYPE_LABELS[priceType] ?? priceType ?? '—';

const pullStatusLabel = status => GSE_PULL_STATUS_LABELS[status] ?? status ?? '—';

const toMillis = value => {
  const time = value ? new Date(value).getTime() : NaN;

  return Number.isNaN(time) ? null : time;
};

// One GseFeedFreshness -> the Feeds & Sources feed-status row. Age is the age of the feed's latest
// QUOTABLE pull (the data currently serving pricing) — null/'—' for a feed that has never gone
// Quotable; that feed is definitionally stale (nothing servable) and its last-attempt status tells the
// operator why. All values are read straight through — nothing is fabricated.
export const mapFeedFreshnessToView = feed => {
  const ageMinutes = parseTimeSpanMinutes(feed.age);

  return {
    id: `${feed.sellerId}:${feed.priceType}`,
    sellerId: feed.sellerId,
    priceType: feed.priceType,
    priceTypeLabel: priceTypeLabel(feed.priceType),
    kind: feed.kind,
    lastQuotableAt: feed.responseDateTime ?? null,
    ageLabel: _.isNil(ageMinutes) ? null : formatDuration(ageMinutes),
    isStale: !!feed.isStale,
    lastAttemptStatus: feed.lastAttemptStatus,
    lastAttemptStatusLabel: pullStatusLabel(feed.lastAttemptStatus),
    lastAttemptAt: feed.lastAttemptDateImported ?? null,
    quarantineStreak: feed.quarantineStreak ?? 0,
  };
};

// One GsePricingPullReviewItem -> a Feeds & Sources "Recent pulls" history row. `channel` is the
// GsePriceType the pull came from; gapSummary is the human completeness note the pull state machine
// already wrote. Read-through only.
export const mapPullHistoryItemToView = item => ({
  id: item.gsePricingPullId,
  sellerId: item.sellerId,
  priceType: item.channel,
  priceTypeLabel: priceTypeLabel(item.channel),
  status: item.status,
  statusLabel: pullStatusLabel(item.status),
  effectiveDate: item.effectiveDate ?? null,
  responseDateTime: item.responseDateTime ?? null,
  isComplete: !!item.isComplete,
  gapSummary: item.gapSummary || '',
});

// One held pull for the review list: the history row (so "View grid" opens the same drawer) plus why
// it is held and whether a person may release it. Reasons arrive ordered (market-closed first).
export const mapHeldPullToView = item => ({
  ...mapPullHistoryItemToView(item),
  dateImported: item.dateImported ?? null,
  reasons: (item.reasons ?? []).map((reason, index) => ({
    id: `${reason.code}-${index}`,
    code: reason.code,
    title: reason.title || reason.code,
    severity: reason.severity ?? null,
    observed: reason.observed ?? null,
    threshold: reason.threshold ?? null,
    detail: reason.detail ?? null,
  })),
  marketClosed: !!item.marketClosed,
  canPromote: !!item.canPromote,
  cannotPromoteReason: item.cannotPromoteReason ?? null,
});

// Composes GET freshness (current per-feed) + the merged GET pulls?status=* history (every previous
// pull, all statuses) into the single read-only view the Feeds & Sources page renders. Feeds are
// ordered Seller then PriceType; pulls newest-first by ResponseDateTime (a pull with an unreadable
// timestamp sorts last rather than jumping the list). Each input degrades independently upstream, so a
// missing piece is an empty array here — never fabricated.
export const mapFeedPulls = ({ freshness, pulls } = {}) => {
  const feeds = (freshness ?? [])
    .map(mapFeedFreshnessToView)
    .sort((a, b) => a.sellerId.localeCompare(b.sellerId) || a.priceType.localeCompare(b.priceType));

  const historyRows = (pulls ?? []).map(mapPullHistoryItemToView).sort((a, b) => {
    const aTime = toMillis(a.responseDateTime);
    const bTime = toMillis(b.responseDateTime);

    if (aTime === bTime) {
      return 0;
    }

    // Unreadable timestamps sort last (newest-first list).
    if (_.isNil(aTime)) {
      return 1;
    }

    if (_.isNil(bTime)) {
      return -1;
    }

    return bTime - aTime;
  });

  return { feeds, pulls: historyRows };
};

const RATE_SHEET_PRODUCT_ID_PREFIX = 'rs';

export const buildRateSheetExternalProductId = ({ priceSourceId, productKey }) =>
  `${RATE_SHEET_PRODUCT_ID_PREFIX}:${String(priceSourceId).replaceAll('-', '').toLowerCase()}:${productKey}`;

export const mapRateSheetProductToMappingFields = ({ priceSource, product }) => ({
  externalProductId: buildRateSheetExternalProductId({
    priceSourceId: priceSource.priceSourceId,
    productKey: product.productKey,
  }),
  externalProductName: `${priceSource.name} · ${product.displayName || product.productKey}`,
  investorId: priceSource.priceSourceId,
  investorName: priceSource.name,
});
